import React, { useContext, useEffect, useState } from "react";
import OwTitle from "../OwTitle/OwTitle";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import OwSortHeader from "../OwSortHeader/OwSortHeader";
import UIService from "../../../services/UIService";
import * as _ from "lodash-es";
import OwTranslate from "../OwTranslate/OwTranslate";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IOnGetOrdersPickListDTO from "../../../models/DTO/IOnGetOrdersPickListDTO";
import SortOrderEnum from "../../../models/SortOrderEnum";
import moment from "moment";
import IGetOrdersPickListDTO from "../../../models/DTO/IGetOrdersPickListDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import queryString from "query-string";
import { useLocation, useNavigate } from "react-router-dom";
import "./SimplePickList.scss";
import PickListComponentEnum from "../../../models/PickListComponentEnum";
import { PreformPickList } from "../../custom/PreformPickList/PreformPickList";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import OwPrintButton from "../OwPrintButton/OwPrintButton";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import { Parser } from "@json2csv/plainjs";
import { IOption } from "../../../models/IOption";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { useDailyRouteGroups } from "../../../hooks/useDailyRouteGroups";
import { DailyRouteCoverSheet } from "../DailyRouteCoverSheet/DailyRouteCoverSheet";
import { OwViewOptionsPanel } from "../OwViewOptionsPanel/OwViewOptionsPanel";
import { usePickListViewOptions } from "../../../hooks/pick_list/usePickListViewOptions";
import { usePickListViewOptionsUI } from "../../../hooks/pick_list/usePickListViewOptionsUI";
import SupplierService from "../../../services/SupplierService";
import IUpdateSupplierSettingsDTO from "../../../models/DTO/IUpdateSupplierSettingsDTO";
import IOnUpdateSupplierSettingsDTO from "../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IOrder_Item_PickListDTO from "../../../models/DTO/IOrder_Item_PickListDTO";

const UNCATEGORIZED_GROUP = "Uncategorized";

type Props = {};

export const SimplePickList: React.FC<Props> = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const location = useLocation();
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const isFromDailyRoutes = location.pathname.includes("routes/daily");
    const { dailyRouteGroups, loading: dailyRoutesLoading, hasDailyRoutes } = useDailyRouteGroups();

    const getNavItems = (): IBreadcrumbs[] => {
        const items: IBreadcrumbs[] = [];

        if (isFromDailyRoutes) {
            items.push({ title: "daily_routes", url: "/routes/daily" });
        } else {
            items.push({ title: "dashboard", url: "/dashboard" });
            items.push({ title: "orders", url: "/orders" });
        }

        items.push({ title: "pick_list", url: "" });

        return items;
    };

    const [pickListData, setPickListData] = useState<IOnGetOrdersPickListDTO>();
    const [groupedPickListData, setGroupedPickListData] = useState<Map<string, IOnGetOrdersPickListDTO>>(new Map());
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);
    const [sortCol, setSortCol] = useState<string>("updated_at");
    const [navItems, setNavItems] = useState(getNavItems());
    const [template, setTemplate] = useState<"STANDARD" | "MINIMAL">("STANDARD");
    const [printOrders, setPrintOrders] = useState(true);

    const supplierSettings = SupplierService.getSupplierSettings(supplierModel);
    const [viewOptions, handleViewOptionChange] = usePickListViewOptions(supplierSettings);
    const optionsPanelViewOptions = usePickListViewOptionsUI(viewOptions);

    const packingListTemplates: IOption[] = [
        { value: "STANDARD", label: "8.5x11" },
        { value: "MINIMAL", label: "4x6" },
    ];

    useEffect(() => {
        socket.on(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSupplierSettings);

        if (!hasDailyRoutes) {
            socket.on(EndpointsEnum.OrderOnGetOrdersPickList, onGetOrdersPickList);
            getPickList();
        }

        return () => {
            socket.off(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSupplierSettings);
            socket.off(EndpointsEnum.OrderOnGetOrdersPickList, onGetOrdersPickList);
        };
    }, []);

    // Sequential socket calls per daily route group
    useEffect(() => {
        if (!hasDailyRoutes || dailyRoutesLoading || dailyRouteGroups.length === 0) return;

        let currentGroupIndex = 0;
        const results = new Map<string, IOnGetOrdersPickListDTO>();

        const handleGroupResponse = (data: IOnGetOrdersPickListDTO) => {
            const group = dailyRouteGroups[currentGroupIndex];
            results.set(group.dailyRoute.guid, data);
            currentGroupIndex++;

            if (currentGroupIndex < dailyRouteGroups.length) {
                emitPickListForGroup(dailyRouteGroups[currentGroupIndex]);
            } else {
                socket.off(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
                setGroupedPickListData(new Map(results));
            }
        };

        const emitPickListForGroup = (group: { orderGuids: string[] }) => {
            const pickListOrders = group.orderGuids.map((guid) => ({ guid }));
            const payload: IGetOrdersPickListDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel!.guid,
                orders: pickListOrders,
            };

            switch (supplierModel.pick_list_component) {
                case PickListComponentEnum.PREFORM:
                    payload.dontGroupItems = true;
                    break;

                default:
                    payload.includeRootCategories = true;
                    break;
            }

            socket.emit(EndpointsEnum.OrderGetOrdersPickList, payload);
        };

        socket.on(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
        emitPickListForGroup(dailyRouteGroups[0]);

        return () => {
            socket.off(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
        };
    }, [hasDailyRoutes, dailyRoutesLoading, dailyRouteGroups]);

    const getOrderGuids = () => {
        const queryStringValues = queryString.parse(location.search);

        const qsOrders = queryStringValues.orders;

        if (_.isString(qsOrders)) {
            return qsOrders?.split(",");
        } else {
            return [];
        }
    };

    const getPickList = () => {
        const orderGuidStrings = getOrderGuids();

        const pickListOrders: { guid: string }[] = [];

        for (const str of orderGuidStrings) {
            pickListOrders.push({ guid: str });
        }

        const payload: IGetOrdersPickListDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel!.guid,
            orders: pickListOrders,
        };

        switch (supplierModel.pick_list_component) {
            case PickListComponentEnum.PREFORM:
                payload.dontGroupItems = true;
                break;

            default:
                payload.includeRootCategories = true;
                break;
        }

        socket.emit(EndpointsEnum.OrderGetOrdersPickList, payload);
    };

    const onGetOrdersPickList = (data: IOnGetOrdersPickListDTO) => {
        setPickListData(data);
    };

    const onUpdateSupplierSettings = (data: IOnUpdateSupplierSettingsDTO) => {
        const message: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_updated_settings",
        };

        uiContext.notifyFn(null, message);
    };

    const saveViewOptions = () => {
        const payload: IUpdateSupplierSettingsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            // @ts-expect-error partial settings payload
            settings: {
                simple_pick_list: {
                    show_sku: viewOptions.showSku,
                    show_item_name: viewOptions.showItemName,
                    show_description: viewOptions.showDescription,
                    show_control: viewOptions.showControl,
                    show_item_location: viewOptions.showItemLocation,
                    group_by_root_category: viewOptions.groupByRootCategory,
                },
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
    };

    let tableTheme = "";

    if (!UIService.isMainViewLight()) {
        tableTheme = "table-dark text-white";
    }

    const getItemTotalQty = (item: IOrder_Item_PickListDTO) => {
        let totalQty = Number(item.total_qty);

        if (supplierModel && supplierModel.has_order_bonus) {
            totalQty += Number(item.total_bonus_qty_frozen);
        }

        return totalQty;
    };

    const groupItemsByRootCategory = (data: IOnGetOrdersPickListDTO): Map<string, IOrder_Item_PickListDTO[]> => {
        const groups = new Map<string, IOrder_Item_PickListDTO[]>();

        for (const item of data.orderItems) {
            const rootName = data.rootCategoryMap?.[item.catalog_item_id]?.name || UNCATEGORIZED_GROUP;

            if (!groups.has(rootName)) {
                groups.set(rootName, []);
            }

            groups.get(rootName)!.push(item);
        }

        const sortedGroupNames = Array.from(groups.keys()).sort((a, b) => {
            if (a === UNCATEGORIZED_GROUP) {
                return 1;
            }

            if (b === UNCATEGORIZED_GROUP) {
                return -1;
            }

            return a.localeCompare(b);
        });

        return new Map(sortedGroupNames.map((groupName) => [groupName, groups.get(groupName)!]));
    };

    // We have to replace any pound symbols, otherwise it'll break the download data when
    // outputing to CSV file.
    const sanitizeCsvValue = (value: string) => {
        return value.indexOf("#") >= 0 ? value.replaceAll("#", "No.") : value;
    };

    const buildCsvHeaderRow = () => {
        const headerRow: string[] = [];

        if (viewOptions.groupByRootCategory) {
            headerRow.push("Root Category");
        }

        headerRow.push("Qty");

        if (viewOptions.showSku) {
            headerRow.push("SKU");
        }

        if (viewOptions.showItemName) {
            headerRow.push("Name");
        }

        if (viewOptions.showDescription) {
            headerRow.push("Description");
        }

        if (viewOptions.showItemLocation) {
            headerRow.push("Item Location");
        }

        if (viewOptions.showControl) {
            headerRow.push("Qty. Picked");
        }

        return headerRow;
    };

    const buildCsvItemRow = (item: IOrder_Item_PickListDTO, rootCategoryName?: string) => {
        const row: { [key: string]: string } = {};

        if (viewOptions.groupByRootCategory) {
            row["Root Category"] = sanitizeCsvValue(rootCategoryName || UNCATEGORIZED_GROUP);
        }

        row["Qty"] = `${getItemTotalQty(item)}`;

        if (viewOptions.showSku) {
            row["SKU"] = sanitizeCsvValue(item.Catalog_Item?.sku || "");
        }

        if (viewOptions.showItemName) {
            row["Name"] = sanitizeCsvValue(item.Catalog_Item?.name || "");
        }

        if (viewOptions.showDescription) {
            row["Description"] = sanitizeCsvValue(item.Catalog_Item?.description || "");
        }

        if (viewOptions.showItemLocation) {
            row["Item Location"] = `${item.Catalog_Item?.row || ""} ${item.Catalog_Item?.bin || ""}`.trim();
        }

        if (viewOptions.showControl) {
            row["Qty. Picked"] = "";
        }

        return row;
    };

    const buildCsvRowsFromPickListData = (data: IOnGetOrdersPickListDTO) => {
        let pickItemsArr: { [key: string]: string }[] = [];

        if (viewOptions.groupByRootCategory) {
            for (const [groupName, groupItems] of groupItemsByRootCategory(data)) {
                pickItemsArr = [...pickItemsArr, ...groupItems.map((item) => buildCsvItemRow(item, groupName))];
            }
        } else {
            pickItemsArr = data.orderItems.map((item) => buildCsvItemRow(item));
        }

        const orders =
            data.orders.map((order) => {
                const vendor = sanitizeCsvValue(order.vendor || "");

                return { Qty: `${order.name} (${vendor})` };
            }) || [];

        return [...pickItemsArr, ...orders];
    };

    const exportPickListCSV = () => {
        const headerRow = buildCsvHeaderRow();

        let rows: any[] = [];

        if (hasDailyRoutes && groupedPickListData.size > 0) {
            for (const [, data] of groupedPickListData) {
                rows = [...rows, ...buildCsvRowsFromPickListData(data)];
            }
        } else if (pickListData) {
            rows = buildCsvRowsFromPickListData(pickListData);
        }

        const parser = new Parser({ fields: headerRow });
        const csvContent = parser.parse(rows);

        const yearMonthDay = moment().format("YYYY-MM-DD");

        const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${yearMonthDay}-Pick-List.csv`);

        link.click();
    };

    const sortPickList = (newSortCol: string, newSortOrder: SortOrderEnum) => {
        // Sortable headers render before the socket response; nothing to sort yet.
        if (!pickListData) {
            return;
        }

        let orderItems = _.sortBy(pickListData.orderItems, (item) => {
            switch (newSortCol) {
                case "item_location":
                    return `${item.Catalog_Item?.row || ""} ${item.Catalog_Item?.bin || ""}`.trim();

                default:
                    // @ts-expect-error dynamic catalog item property
                    return item.Catalog_Item[newSortCol];
            }
        });

        if (pickListData && orderItems) {
            if (newSortOrder === SortOrderEnum.DESC) {
                orderItems = orderItems.reverse();
            }

            pickListData.orderItems = orderItems;

            setSortOrder(newSortOrder);
            setSortCol(newSortCol);
            setPickListData(pickListData);
        }
    };

    const ordersDetail = _.map(pickListData?.orders, (order) => {
        return (
            <div key={order.guid} className={"col-3"}>
                <i className={"far fa-square"} /> {order?.name} ({order?.vendor})
            </div>
        );
    });

    const orders = (
        <div className={printOrders ? "" : "d-print-none"}>
            <div className={"d-print-none mt-4"}>
                <OwCheckbox
                    fn={() => {
                        setPrintOrders(!printOrders);
                    }}
                    checked={printOrders}
                    label={"print_orders_detail"}
                />
            </div>
            <OwTitle className={"mt-3"} title={"orders"} tag={"h5"} />
            <div className={"row d-flex"}>{ordersDetail}</div>
        </div>
    );

    const visibleColCount =
        1 +
        (viewOptions.showSku ? 1 : 0) +
        (viewOptions.showItemName ? 1 : 0) +
        (viewOptions.showDescription ? 1 : 0) +
        (viewOptions.showItemLocation ? 1 : 0) +
        (viewOptions.showControl ? 1 : 0);

    const selectTemplate = (option: IOption) => {
        setTemplate(option.value as "STANDARD" | "MINIMAL");
    };

    const viewPackingLists = () => {
        if (hasDailyRoutes) {
            const params = new URLSearchParams(location.search);
            const drParam = params.get("dailyRoutes") || "";
            navigate(`/routes/daily/packing_list?dailyRoutes=${drParam}`);
        } else {
            const orderGuidStrings = getOrderGuids();
            const basePath = isFromDailyRoutes ? "/routes/daily" : "/orders";
            navigate(`${basePath}/packing_list?orders=${orderGuidStrings?.join(",")}`);
        }
    };

    const renderItemTbody = (item: IOrder_Item_PickListDTO) => {
        let extendedDetails: any = null;

        if (item.note) {
            extendedDetails = (
                <tr>
                    <td></td>
                    <td colSpan={visibleColCount - 1}>
                        <b>
                            <OwTranslate defaultMessage={"note"} id={"note"} />:
                        </b>{" "}
                        {item.note}
                    </td>
                </tr>
            );
        }

        return (
            <tbody key={item.guid}>
                <tr>
                    <td>{getItemTotalQty(item)}</td>
                    {viewOptions.showSku ? <td>{item.Catalog_Item?.sku}</td> : null}
                    {viewOptions.showItemName ? <td>{item.Catalog_Item?.name}</td> : null}
                    {viewOptions.showDescription ? <td>{item.Catalog_Item?.description}</td> : null}
                    {viewOptions.showItemLocation ? (
                        <td>
                            {item.Catalog_Item?.row || item.Catalog_Item?.bin ? (
                                <div className={"d-flex"}>
                                    <div className={"me-2"}>{item.Catalog_Item?.row}</div>
                                    <div>{item.Catalog_Item?.bin}</div>
                                </div>
                            ) : (
                                <>&nbsp;</>
                            )}
                        </td>
                    ) : null}
                    {viewOptions.showControl ? (
                        <td>
                            {template === "STANDARD" ? (
                                <div>
                                    <OwLabel title={"qty_picked"} />:
                                </div>
                            ) : null}
                        </td>
                    ) : null}
                </tr>
                {extendedDetails}
            </tbody>
        );
    };

    const renderOrdersDetail = (data: IOnGetOrdersPickListDTO) => {
        const groupOrdersDetail = _.map(data.orders, (order) => {
            return (
                <div key={order.guid} className={"col-3"}>
                    <i className={"far fa-square"} /> {order?.name} ({order?.vendor})
                </div>
            );
        });

        return (
            <div className={printOrders ? "" : "d-print-none"}>
                <OwTitle className={"mt-3"} title={"orders"} tag={"h5"} />
                <div className={"row d-flex"}>{groupOrdersDetail}</div>
            </div>
        );
    };

    const renderPickListTable = (data: IOnGetOrdersPickListDTO | undefined, sortable: boolean) => {
        const orderItems = data?.orderItems || [];
        const pickTotalQty = _.sumBy(orderItems, getItemTotalQty);

        let bodySections: React.ReactNode[] = [];

        if (data && viewOptions.groupByRootCategory) {
            for (const [groupName, groupItems] of groupItemsByRootCategory(data)) {
                bodySections.push(
                    <tbody key={`group-header-${groupName}`}>
                        <tr className={"group-header"}>
                            <td colSpan={visibleColCount}>
                                {groupName === UNCATEGORIZED_GROUP ? (
                                    <OwTranslate defaultMessage={"Uncategorized"} id={"uncategorized"} />
                                ) : (
                                    groupName
                                )}
                            </td>
                        </tr>
                    </tbody>
                );

                bodySections = [...bodySections, ...groupItems.map(renderItemTbody)];

                bodySections.push(
                    <tbody key={`group-total-${groupName}`}>
                        <tr>
                            <td colSpan={visibleColCount}>
                                {_.sumBy(groupItems, getItemTotalQty)} <OwLabel title={"total"} noBottomMargin={true} />
                            </td>
                        </tr>
                    </tbody>
                );
            }
        } else {
            bodySections = orderItems.map(renderItemTbody);
        }

        return (
            <div className={"table-responsive"}>
                <table className={"orders-table table " + tableTheme}>
                    <thead>
                        <tr>
                            <th>
                                <OwLabel title={"qty"} rowHasSort={true} />
                            </th>
                            {viewOptions.showSku ? (
                                <th>
                                    {sortable ? (
                                        <OwSortHeader
                                            title={"sku"}
                                            currentSortCol={sortCol}
                                            currentSortOrder={sortOrder}
                                            sortFn={sortPickList}
                                        />
                                    ) : (
                                        <OwLabel title={"sku"} rowHasSort={true} />
                                    )}
                                </th>
                            ) : null}
                            {viewOptions.showItemName ? (
                                <th>
                                    <OwLabel title={"name"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {viewOptions.showDescription ? (
                                <th>
                                    <OwLabel title={"description"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {viewOptions.showItemLocation ? (
                                <th>
                                    {sortable ? (
                                        <OwSortHeader
                                            title={"item_location"}
                                            currentSortCol={sortCol}
                                            currentSortOrder={sortOrder}
                                            sortFn={sortPickList}
                                        />
                                    ) : (
                                        <OwLabel title={"item_location"} rowHasSort={true} />
                                    )}
                                </th>
                            ) : null}
                            {viewOptions.showControl ? (
                                <th>
                                    <OwLabel
                                        className={"no-wrap"}
                                        title={template === "STANDARD" ? "control" : "qty_picked"}
                                        rowHasSort={true}
                                    />
                                </th>
                            ) : null}
                        </tr>
                    </thead>
                    {bodySections}
                    <tbody>
                        <tr>
                            <td colSpan={visibleColCount}>
                                {pickTotalQty} <OwLabel title={"total"} noBottomMargin={true} />
                            </td>
                        </tr>
                    </tbody>
                    <tfoot />
                </table>
            </div>
        );
    };

    const defaultRenderer = () => {
        // Grouped daily routes mode
        if (hasDailyRoutes && groupedPickListData.size > 0) {
            return (
                <div className={"SimplePickList " + template}>
                    <div className={"mt-2 mb-2 d-flex justify-content-between align-items-center flex-wrap"}>
                        <div className={"d-inline-flex"}>
                            <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                        </div>
                        <div className={"d-print-none"}>
                            <OwPrintButton />
                            <div className={"ms-auto w-10r"}>
                                <OwSelectOption
                                    valueProp={"value"}
                                    titleProp={"label"}
                                    fn={selectTemplate}
                                    name={"template"}
                                    options={packingListTemplates}
                                    emitObject={true}
                                    selectedOption={template}
                                    hasSelectedOptionProp={false}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={"d-print-none"}>
                        <OwButton title={"view_packing_lists"} fn={viewPackingLists} className={"btn-primary ms-2"} />
                        <OwButton
                            leftIconClass={"fad fa-download"}
                            title={"export_to_csv"}
                            fn={exportPickListCSV}
                            className={"btn-primary ms-2"}
                        />
                        <div className={"mt-2"}>
                            <OwCheckbox
                                fn={() => {
                                    setPrintOrders(!printOrders);
                                }}
                                checked={printOrders}
                                label={"print_orders_detail"}
                            />
                        </div>
                    </div>
                    {dailyRouteGroups.map((group) => {
                        const groupData = groupedPickListData.get(group.dailyRoute.guid);
                        if (!groupData) return null;

                        return (
                            <div key={group.dailyRoute.guid} className={"PickListGroup"}>
                                <DailyRouteCoverSheet
                                    dailyRouteName={group.dailyRoute.name || ""}
                                    routeName={group.dailyRoute.Route?.name || ""}
                                    routeDate={moment(group.dailyRoute.route_date).format("MM/DD/YYYY")}
                                    driverName={group.dailyRoute.Driver?.name || "-"}
                                    vehicleName={group.dailyRoute.Vehicle?.name || "-"}
                                    orderCount={group.orderGuids.length}
                                />
                                <div>
                                    <OwTitle title={"pick_list"} />
                                    {renderOrdersDetail(groupData)}
                                    {renderPickListTable(groupData, false)}
                                </div>
                            </div>
                        );
                    })}
                    <OwViewOptionsPanel
                        saveFn={saveViewOptions}
                        showSaveBtn={true}
                        viewOptions={optionsPanelViewOptions}
                        onChangeFn={handleViewOptionChange}
                    />
                </div>
            );
        }

        // Standard single pick list mode
        return (
            <div className={"SimplePickList " + template}>
                <div className={"mt-2 mb-2 d-flex justify-content-between align-items-center flex-wrap"}>
                    <div className={"d-inline-flex"}>
                        <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                    </div>
                    <div className={"d-print-none"}>
                        <OwPrintButton />
                        <div className={"ms-auto w-10r"}>
                            <OwSelectOption
                                valueProp={"value"}
                                titleProp={"label"}
                                fn={selectTemplate}
                                name={"template"}
                                options={packingListTemplates}
                                emitObject={true}
                                selectedOption={template}
                                hasSelectedOptionProp={false}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <OwTitle title={"pick_list"} />
                    <OwButton
                        leftIconClass={"fad fa-download"}
                        title={"export_to_csv"}
                        fn={exportPickListCSV}
                        className={"btn-primary d-print-none"}
                    />
                    <OwButton title={"view_packing_lists"} fn={viewPackingLists} className={"btn-primary ms-2 d-print-none"} />
                    {orders}
                    {renderPickListTable(pickListData, true)}
                </div>
                <OwViewOptionsPanel
                    saveFn={saveViewOptions}
                    showSaveBtn={true}
                    viewOptions={optionsPanelViewOptions}
                    onChangeFn={handleViewOptionChange}
                />
            </div>
        );
    };

    const preFormComponent = () => {
        return (
            <div>
                <PreformPickList notifyFn={uiContext.notifyFn} socket={socket} data={pickListData} toggleMenu={uiContext.toggleMenu} />
            </div>
        );
    };

    {
        switch (supplierModel.pick_list_component) {
            case PickListComponentEnum.PREFORM:
                return preFormComponent();

            default:
                return defaultRenderer();
        }
    }
};
